import React from "react";
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps,
} from "@mui/material";

interface StandardAutocompleteProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    "renderInput"
  > {
  label?: string;
  placeholder?: string;
  textFieldProps?: Partial<TextFieldProps>;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: 1,
    cursor: "pointer",
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
      borderWidth: 1.5,
      transition: "border-color 0.2s ease",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.6)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.8)",
        borderWidth: 2,
      },
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.03)",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.12)",
      },
    },
  },
  "& .MuiInputBase-input": {
    color: "white",
    fontWeight: 500,
    cursor: "pointer",
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5)",
      opacity: 1,
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-focused": {
      color: "white",
    },
  },
  "& .MuiAutocomplete-endAdornment": {
    "& .MuiSvgIcon-root": {
      color: "rgba(255, 255, 255, 0.6)",
    },
  },
};

const dropdownSx = {
  "& .MuiAutocomplete-paper": {
    backgroundColor: "#1e1e1e",
    backgroundImage: "none",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: 2,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
    mt: 0.5,
  },
  "& .MuiAutocomplete-listbox": {
    padding: "4px",
    "& .MuiAutocomplete-option": {
      color: "white",
      borderRadius: 1,
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      padding: "10px 12px",
      '&[aria-selected="true"]': {
        backgroundColor: "rgba(255, 255, 255, 0.12)",
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
      },
      '&[aria-selected="true"].Mui-focused': {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
      },
    },
  },
  "& .MuiAutocomplete-noOptions": {
    color: "rgba(255, 255, 255, 0.5)",
  },
};

export const StandardAutocomplete = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  label,
  placeholder,
  textFieldProps,
  sx,
  slotProps,
  ...rest
}: StandardAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  return (
    <Autocomplete
      sx={{
        cursor: "pointer",
        ...sx,
      }}
      slotProps={{
        ...slotProps,
        popper: {
          ...slotProps?.popper,
          sx: {
            ...dropdownSx,
            ...(slotProps?.popper as any)?.sx,
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          {...textFieldProps}
          sx={{
            ...inputSx,
            ...textFieldProps?.sx,
          }}
        />
      )}
      {...rest}
    />
  );
};
